import { useEffect, useState, type RefObject } from "react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

/**
 * The mobile add-to-cart bar, fixed to the bottom of the viewport.
 *
 * Shows only once the inline button has scrolled out of view, so the page
 * never has two identical buttons on screen at once. Starts hidden: the page
 * is prerendered, and the server has no viewport to observe — the first client
 * render must agree with the static HTML.
 */
export function BuyBar({
  anchor,
  observeKey,
  name,
  summary,
  label,
  disabled,
  onBuy,
  href,
}: {
  /** The inline buy button's container; the bar appears when it leaves the viewport. */
  anchor: RefObject<HTMLElement | null>;
  /** Re-observe when this changes — the anchor remounts per product. */
  observeKey: string;
  name: string;
  summary?: string;
  label: string;
  disabled: boolean;
  onBuy: () => void;
  /**
   * Where to send the shopper instead of adding to the cart. Set when the
   * selection is made to order: there is no cart line to create, so the bar
   * has to be a link rather than a button that does nothing.
   */
  href?: string;
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = anchor.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => setVisible(entry ? !entry.isIntersecting : false),
      { threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [anchor, observeKey]);

  return (
    <div
      aria-hidden={!visible}
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 border-t border-ink/10 bg-white/95 backdrop-blur transition-transform duration-200 lg:hidden",
        visible ? "translate-y-0" : "translate-y-full",
      )}
    >
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-5 py-3 sm:px-8">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{name}</p>
          {summary && <p className="truncate text-xs text-ink/60">{summary}</p>}
        </div>
        {href ? (
          <Button size="sm" asChild>
            <a href={href} target="_blank" rel="noopener noreferrer" tabIndex={visible ? 0 : -1} data-wa-placement="buy_bar">
              {label}
            </a>
          </Button>
        ) : (
          <Button variant="cart" size="sm" disabled={disabled} onClick={onBuy} tabIndex={visible ? 0 : -1}>
            {label}
          </Button>
        )}
      </div>
    </div>
  );
}
